import axios from 'axios';

const isProd = process.env.NODE_ENV === 'production';
const BACKEND_URL = isProd ? '' : (process.env.REACT_APP_BACKEND_URL || '');
const API_URL = BACKEND_URL ? BACKEND_URL + '/api' : '/api';

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors (logout on unauthorized)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

// ===== AUTH API =====
export const authAPI = {
  register: async (name, email, password) => {
    const response = await api.post('/auth/register', { name, email, password });
    return response.data;
  },

  login: async (email, password, remember_me = false) => {
    const response = await api.post('/auth/login', { email, password, remember_me });
    if (response.data.access_token) {
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
    return response.data;
  },

  forgotPassword: async (email) => {
    const response = await api.post('/auth/forgot-password', { email });
    return response.data;
  },

  resetPassword: async (token, new_password) => {
    const response = await api.post('/auth/reset-password', { token, new_password });
    return response.data;
  },

  changePassword: async (current_password, new_password) => {
    const response = await api.post('/auth/change-password', { current_password, new_password });
    return response.data;
  },

  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  },

  getCurrentUser: () => {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  },

  getMe: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },
};

// ===== FOOD API =====
export const foodAPI = {
  getAll: async (search = '') => {
    const response = await api.get('/foods', { params: { search } });
    return response.data;
  },

  create: async (foodData) => {
    const response = await api.post('/foods', foodData);
    return response.data;
  },
  
  update: async (foodId, foodData) => {
    const response = await api.put(`/foods/${foodId}`, foodData);
    return response.data;
  },

  delete: async (foodId) => {
    const response = await api.delete(`/foods/${foodId}`);
    return response.data;
  },

  bulkImport: async (items) => {
    const response = await api.post('/foods/bulk', items);
    return response.data;
  },
};

// ===== PARENTERAL API =====
export const parenteralAPI = {
  getAll: async (search = '') => {
    const response = await api.get('/parenteral', { params: { search } });
    return response.data;
  },

  create: async (itemData) => {
    const response = await api.post('/parenteral', itemData);
    return response.data;
  },
  
  update: async (itemId, itemData) => {
    const response = await api.put(`/parenteral/${itemId}`, itemData);
    return response.data;
  },

  delete: async (itemId) => {
    const response = await api.delete(`/parenteral/${itemId}`);
    return response.data;
  },

  bulkImport: async (items) => {
    const response = await api.post('/parenteral/bulk', items);
    return response.data;
  },
};

// ===== PATIENT RECORDS API =====
export const recordsAPI = {
  getAll: async () => {
    const response = await api.get('/records');
    return response.data;
  },

  getById: async (recordId) => {
    const response = await api.get(`/records/${recordId}`);
    return response.data;
  },

  create: async (recordData) => {
    const response = await api.post('/records', recordData);
    return response.data;
  },

  delete: async (recordId) => {
    const response = await api.delete(`/records/${recordId}`);
    return response.data;
  },
};

// ===== PATIENTS API =====
export const patientsAPI = {
  getAll: async () => {
    const response = await api.get('/patients');
    return response.data;
  },

  getById: async (patientId) => {
    const response = await api.get(`/patients/${patientId}`);
    return response.data;
  },

  create: async (patientData) => {
    const response = await api.post('/patients', patientData);
    return response.data;
  },

  update: async (patientId, patientData) => {
    const response = await api.put(`/patients/${patientId}`, patientData);
    return response.data;
  },

  delete: async (patientId) => {
    const response = await api.delete(`/patients/${patientId}`);
    return response.data;
  },

  getRecords: async (patientId) => {
    const response = await api.get(`/patients/${patientId}/records`);
    return response.data;
  },
};

export const patientAPI = patientsAPI;

// ===== ADMIN API =====
export const adminAPI = {
  getPendingUsers: async () => {
    const response = await api.get('/admin/users/pending');
    return response.data;
  },

  getPendingCount: async () => {
    const response = await api.get('/admin/users/pending/count');
    return response.data;
  },

  getAllUsers: async () => {
    const response = await api.get('/admin/users/all');
    return response.data;
  },

  approveUser: async (userId) => {
    const response = await api.post(`/admin/users/${userId}/approve`);
    return response.data;
  },

  rejectUser: async (userId) => {
    const response = await api.post(`/admin/users/${userId}/reject`);
    return response.data;
  },

  deleteUser: async (userId) => {
    const response = await api.delete(`/admin/users/${userId}`);
    return response.data;
  },

  updateUserStatus: async (userId, status) => {
    const response = await api.patch(`/admin/users/${userId}/status?status=${status}`);
    return response.data;
  },

  updateUser: async (userId, updateData) => {
    const response = await api.put(`/admin/users/${userId}`, updateData);
    return response.data;
  },

  extendUserAccess: async (userId, days) => {
    const response = await api.post(`/admin/users/${userId}/extend?days=${days}`);
    return response.data;
  },

  resetUserPassword: async (userId, newPassword) => {
    const response = await api.post(`/admin/users/${userId}/reset-password`, {
      new_password: newPassword
    });
    return response.data;
  },
};

// ===== SETTINGS API =====
export const settingsAPI = {
  getSettings: async () => {
    const response = await api.get('/settings');
    return response.data;
  },

  updateSettings: async (dataOrPhone, message) => {
    let payload = {};
    if (typeof dataOrPhone === 'object' && dataOrPhone !== null) {
      payload = dataOrPhone;
    } else {
      payload = { whatsapp_number: dataOrPhone, whatsapp_message: message };
    }
    const response = await api.put('/settings', payload);
    return response.data;
  },

  testEmail: async (testData) => {
    const response = await api.post('/settings/test-email', testData);
    return response.data;
  },

  getPublicSettings: async () => {
    const response = await api.get('/settings/public');
    return response.data;
  },
};

export default api;
